import { Injectable } from '@angular/core';
import { CustomPatterns } from '@cargos/sprintpay-utils';
import { BuiltInCreditCardType } from 'credit-card-type/dist/types';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Injectable()
export class CreditCardService {
    private cardType: ReplaySubject<BuiltInCreditCardType>;

    constructor() {
        this.cardType = new ReplaySubject<BuiltInCreditCardType>(1);
    }

    setCardType(cardType: BuiltInCreditCardType): void {
        this.cardType.next(cardType);
    }

    getCardType(): Observable<BuiltInCreditCardType> {
        return this.cardType.asObservable();
    }

    getSecurityCodeSize(): Observable<BuiltInCreditCardType['code']['size']> {
        return this.getCardType().pipe(
            map((cardType) => cardType?.code?.size),
            distinctUntilChanged()
        );
    }

    getCreditCardGaps(): Observable<BuiltInCreditCardType['gaps']> {
        return this.getCardType().pipe(map((cardType) => cardType?.gaps));
    }

    getCreditCardLengths(): Observable<BuiltInCreditCardType['lengths']> {
        return this.getCardType().pipe(map((cardType) => cardType?.lengths));
    }

    getSecurityCodeName(): Observable<BuiltInCreditCardType['code']['name']> {
        return this.getCardType().pipe(
            map((cardType) => cardType?.code?.name),
            distinctUntilChanged()
        );
    }

    getRegexCreditCardNumber(): Observable<RegExp> {
        return combineLatest([this.getCreditCardGaps(), this.getCreditCardLengths()]).pipe(
            map(([gaps, lengths]) => {
                const a = new RegExp(
                    CustomPatterns.card_number(gaps, lengths)
                        .map((pattern) => `^${pattern}$`)
                        .join('|')
                );

                return new RegExp(
                    CustomPatterns.card_number(gaps, lengths)
                        .map((pattern) => `^${pattern}$`)
                        .join('|')
                );
            })
        );
    }

    getRegexSecurityCode(): Observable<RegExp> {
        return this.getSecurityCodeSize().pipe(map((size) => new RegExp(`^${CustomPatterns.security_code(size)}$`)));
    }
}
